import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Select, Spin } from 'antd';
import { DefaultOptionType, SelectProps } from 'antd/lib/select';
import cs from 'classnames';

import getPopupContainer from 'utils/getPopupContainer';

import { FormattedOption } from 'types/dictionaries';

import { UPGRADE_TIER_TOOLTIP } from 'constants/uiSettings';

import { SelectValue } from '../../filter.types';
import TooltipAccessWrapper from '../TooltipAccessWrapper';
import styles from './SelectCommon.module.scss';

const { Option } = Select;

interface SelectCommonProps
  extends Omit<SelectProps<SelectValue>, 'value' | 'onChange'> {
  title: string;
  value?: SelectValue | undefined;
  onChange?: (value: SelectValue) => void;
  options?: FormattedOption[];
  loading?: boolean;
  singleMode?: boolean;
  customOptions?: ReactNode;
  errorText?: string;
  isSearchByLabel?: boolean;
  disabledOptionsValues?: string[];
  hasAccessToFilter?: boolean;
  onCheckboxCountrySelect?: () => void;
  disabled?: boolean;
}

const SelectCommon: FC<SelectCommonProps> = ({
  title,
  value,
  onChange,
  options,
  loading = false,
  singleMode = false,
  customOptions,
  status,
  errorText,
  isSearchByLabel = true,
  hasAccessToFilter = false,
  disabledOptionsValues = [],
  onCheckboxCountrySelect,
  disabled = false,
  ...props
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const searchByLabel = (input: string, option?: DefaultOptionType) => {
    return option?.props?.children?.toLowerCase().includes(input.toLowerCase());
  };

  const handleChange = (newValue: SelectValue): void => {
    onChange?.(newValue);
    setSearchValue('');
  };

  const handleCheckboxChange = (checked: boolean, optionValue: string) => {
    if (singleMode) {
      handleChange(checked ? optionValue : '');
    } else {
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = checked
        ? [...currentValue, optionValue]
        : currentValue.filter((val) => val !== optionValue);
      handleChange(newValue);
    }

    onCheckboxCountrySelect?.();
  };

  const filteredOptions = useMemo(() => {
    if (!props.showSearch || !options) return options;

    if (!searchValue) return options;

    return options.filter((option) =>
      (option.label as string)
        ?.toLowerCase()
        .includes(searchValue.toLowerCase())
    );
  }, [options, props.showSearch, searchValue]);

  const dropdownRender = () => (
    <div className={styles.optionsWrapper}>
      {filteredOptions?.map((option) => {
        const isChecked = singleMode
          ? value === option.value
          : Array.isArray(value) && value.includes(option.value);

        return (
          <div
            key={option.value}
            className={cs(styles.optionWrapper, {
              [styles.optionWrapperChecked]: isChecked
            })}
          >
            <Checkbox
              className={styles.optionCheckbox}
              checked={isChecked}
              onChange={(e) =>
                handleCheckboxChange(e.target.checked, option.value)
              }
            >
              {option.label}
            </Checkbox>
          </div>
        );
      })}
    </div>
  );

  const customDropdownRender =
    !singleMode && !customOptions ? dropdownRender : undefined;

  return (
    <div className="item-container">
      <div className={cs('item-title', { disabled: disabled })}>{title}</div>
      <TooltipAccessWrapper
        hasAccessToFilter={hasAccessToFilter}
        message={t(UPGRADE_TIER_TOOLTIP)}
      >
        <Select
          mode={singleMode ? undefined : 'multiple'}
          className="item-select"
          maxTagCount="responsive"
          value={value}
          onChange={handleChange}
          loading={loading}
          filterOption={isSearchByLabel ? searchByLabel : false}
          status={status}
          notFoundContent={
            loading ? (
              <div className="select-spinner-container">
                <Spin size="default" />
              </div>
            ) : null
          }
          getPopupContainer={getPopupContainer}
          disabled={disabled || !hasAccessToFilter}
          popupRender={customDropdownRender}
          onSearch={setSearchValue}
          {...props}
        >
          {customOptions ||
            (filteredOptions &&
              filteredOptions.map(({ value, label }) => (
                <Option
                  key={value}
                  value={value}
                  disabled={disabledOptionsValues.includes(value)}
                >
                  {label}
                </Option>
              )))}
        </Select>
        {status === 'error' && errorText && (
          <div className="select-error-text">{errorText}</div>
        )}
      </TooltipAccessWrapper>
    </div>
  );
};

export default SelectCommon;
